import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  CircularProgress,
  Alert,
  Tooltip,
  FormControlLabel,
  Switch,
} from '@mui/material';
import {
  Add as AddIcon,
  Search as SearchIcon,
  Visibility as ViewIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  PersonAdd as HireIcon,
  Upload as UploadIcon,
  Description as ResumeIcon,
  Close as CloseIcon,
  Inventory2 as ArchiveIcon,
  Unarchive as UnarchiveIcon,
} from '@mui/icons-material';
import { candidatesApi, newHiresApi, onboardingApi, locationsApi } from '../services/api';
import { useAuth } from '../context/AuthContext';
import {
  Candidate,
  CandidateCreate,
  CandidateStatus,
  CandidateSource,
  PositionType,
} from '../types';

const statusColors: Record<CandidateStatus, 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning'> = {
  new: 'info',
  screening: 'primary',
  interview_scheduled: 'warning',
  interview_completed: 'secondary',
  offer_extended: 'primary',
  offer_accepted: 'success',
  hired: 'success',
  rejected: 'error',
  withdrawn: 'default',
};

const statusLabels: Record<CandidateStatus, string> = {
  new: 'New',
  screening: 'Screening',
  interview_scheduled: 'Interview Scheduled',
  interview_completed: 'Interview Completed',
  offer_extended: 'Offer Extended',
  offer_accepted: 'Offer Accepted',
  hired: 'Hired',
  rejected: 'Rejected',
  withdrawn: 'Withdrawn',
};

const sourceLabels: Record<CandidateSource, string> = {
  indeed: 'Indeed',
  linkedin: 'LinkedIn',
  referral: 'Referral',
  walk_in: 'Walk-in',
  company_website: 'Company Website',
  job_fair: 'Job Fair',
  other: 'Other',
};

const interviewTypeLabels: Record<string, string> = {
  phone_screen: 'Phone',
  in_person: 'In Person',
  video: 'Video',
  panel: 'Panel',
  technical: 'Technical',
};

const formatTimeToAMPM = (time: string): string => {
  const [hours, minutes] = time.split(':').map(Number);
  const period = hours >= 12 ? 'PM' : 'AM';
  const hour12 = hours % 12 || 12;
  return `${hour12}:${minutes.toString().padStart(2, '0')} ${period}`;
};

const formatNextInterview = (nextInterview: { date: string; time?: string; type: string } | null | undefined): string => {
  if (!nextInterview) return '-';

  const date = new Date(nextInterview.date + 'T00:00:00');
  const dateStr = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'America/New_York' });
  const timeStr = nextInterview.time ? ` ${formatTimeToAMPM(nextInterview.time)}` : '';
  const typeStr = interviewTypeLabels[nextInterview.type] || nextInterview.type;

  return `${dateStr}${timeStr} (${typeStr})`;
};

const Candidates: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { isManager } = useAuth();

  // State
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<CandidateStatus | ''>('');
  const [locationFilter, setLocationFilter] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [hireDialogOpen, setHireDialogOpen] = useState(false);
  const [selectedCandidate, setSelectedCandidate] = useState<Candidate | null>(null);
  const [error, setError] = useState('');
  const [resumeFile, setResumeFile] = useState<File | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showArchived, setShowArchived] = useState(false);

  // Form state
  const [formData, setFormData] = useState<CandidateCreate>({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    position: '',
    location: undefined,
    source: undefined,
    notes: '',
  });

  // Hire form state
  const [hireData, setHireData] = useState({
    hire_date: new Date().toISOString().split('T')[0],
    position_type_id: undefined as number | undefined,
    department: '',
    manager_id: undefined as number | undefined,
  });

  // Position types query
  const { data: positionTypesData } = useQuery(
    'position-types',
    () => onboardingApi.getPositionTypes()
  );
  const positionTypes = positionTypesData?.position_types?.filter((pt: PositionType) => pt.is_active) || [];

  // Locations query
  const { data: locationsData } = useQuery(['locations'], () =>
    locationsApi.getLocations({ is_active: true }));
  const locationOptions = locationsData?.locations.map((l) => l.name) || [];

  // Query
  const { data, isLoading, refetch } = useQuery(
    ['candidates', page, rowsPerPage, search, statusFilter, locationFilter, showArchived],
    () =>
      candidatesApi.getCandidates({
        skip: page * rowsPerPage,
        limit: rowsPerPage,
        search: search || undefined,
        status: statusFilter || undefined,
        location: locationFilter || undefined,
        include_archived: showArchived || undefined,
      }),
    { keepPreviousData: true }
  );

  // Mutations
  const createMutation = useMutation(candidatesApi.createCandidate, {
    onSuccess: () => {
      queryClient.invalidateQueries(['candidates']);
      handleCloseDialog();
    },
    onError: (err: any) => {
      setError(err.response?.data?.detail || 'Failed to create candidate');
    },
  });

  const archiveMutation = useMutation(
    ({ id, isArchived }: { id: number; isArchived: boolean }) =>
      candidatesApi.archiveCandidate(id, isArchived),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['candidates']);
      },
      onError: (err: any) => {
        setError(err.response?.data?.detail || 'Failed to archive candidate');
      },
    }
  );

  const deleteMutation = useMutation(candidatesApi.deleteCandidate, {
    onSuccess: () => {
      queryClient.invalidateQueries(['candidates']);
    },
    onError: (err: any) => {
      setError(err.response?.data?.detail || 'Failed to delete candidate');
    },
  });

  const hireMutation = useMutation(newHiresApi.createFromCandidate, {
    onSuccess: (newHire) => {
      queryClient.invalidateQueries(['candidates']);
      setHireDialogOpen(false);
      setSelectedCandidate(null);
      navigate(`/new-hires/${newHire.id}`);
    },
    onError: (err: any) => {
      setError(err.response?.data?.detail || 'Failed to convert to new hire');
    },
  });

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setFormData({
      first_name: '',
      last_name: '',
      email: '',
      phone: '',
      position: '',
      location: undefined,
      source: undefined,
      notes: '',
    });
    setResumeFile(null);
    setError('');
  };

  const handleSubmit = async () => {
    if (!formData.first_name || !formData.last_name || !formData.position) {
      setError('First name, last name, and position are required');
      return;
    }

    setIsSubmitting(true);
    setError('');

    try {
      // Clean up empty strings to send null instead
      const cleanedData = {
        ...formData,
        email: formData.email || undefined,
        phone: formData.phone || undefined,
        location: formData.location || undefined,
        notes: formData.notes || undefined,
      };

      // Create the candidate
      const newCandidate = await candidatesApi.createCandidate(cleanedData);

      // If a resume file was selected, upload it
      if (resumeFile) {
        try {
          await candidatesApi.uploadResume(newCandidate.id, resumeFile);
        } catch (uploadErr: any) {
          // Candidate was created but resume upload failed
          console.error('Resume upload failed:', uploadErr);
          setError('Candidate created but resume upload failed. You can upload it from the detail page.');
        }
      }

      queryClient.invalidateQueries(['candidates']);
      handleCloseDialog();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to create candidate');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleHire = () => {
    if (!selectedCandidate || !hireData.hire_date || !hireData.position_type_id) {
      setError('Hire date and position type are required');
      return;
    }
    hireMutation.mutate({
      candidate_id: selectedCandidate.id,
      hire_date: hireData.hire_date,
      position_type_id: hireData.position_type_id,
      department: hireData.department || undefined,
      manager_id: hireData.manager_id,
    });
  };

  const handleArchive = (candidate: Candidate) => {
    const action = candidate.is_archived ? 'unarchive' : 'archive';
    if (window.confirm(`Are you sure you want to ${action} ${candidate.first_name} ${candidate.last_name}?`)) {
      archiveMutation.mutate({ id: candidate.id, isArchived: !candidate.is_archived });
    }
  };

  const handleDelete = (candidate: Candidate) => {
    if (window.confirm(`Are you sure you want to delete ${candidate.first_name} ${candidate.last_name}?`)) {
      deleteMutation.mutate(candidate.id);
    }
  };

  return (
    <Container maxWidth="xl">
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h4" component="h1">
            Candidates
          </Typography>
          {isManager && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setDialogOpen(true)}
            >
              Add Candidate
            </Button>
          )}
        </Box>

        {error && (
          <Alert severity="error" onClose={() => setError('')} sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* Filters */}
        <Paper sx={{ p: 2, mb: 3 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                fullWidth
                size="small"
                placeholder="Search candidates..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                InputProps={{
                  startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />,
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Status</InputLabel>
                <Select
                  value={statusFilter}
                  label="Status"
                  onChange={(e) => setStatusFilter(e.target.value as CandidateStatus | '')}
                >
                  <MenuItem value="">All Statuses</MenuItem>
                  {Object.entries(statusLabels).map(([value, label]) => (
                    <MenuItem key={value} value={value}>
                      {label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Location</InputLabel>
                <Select
                  value={locationFilter}
                  label="Location"
                  onChange={(e) => setLocationFilter(e.target.value)}
                >
                  <MenuItem value="">All Locations</MenuItem>
                  {locationOptions.map((loc) => (
                    <MenuItem key={loc} value={loc}>
                      {loc}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <FormControlLabel
                control={
                  <Switch
                    checked={showArchived}
                    onChange={(e) => { setShowArchived(e.target.checked); setPage(0); }}
                    size="small"
                  />
                }
                label="Show Archived"
              />
            </Grid>
          </Grid>
        </Paper>

        {/* Table */}
        <TableContainer component={Paper}>
          {isLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>Position</TableCell>
                    <TableCell>Location</TableCell>
                    <TableCell>Source</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Next Interview</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data?.candidates.map((candidate) => (
                    <TableRow key={candidate.id} hover sx={candidate.is_archived ? { opacity: 0.6 } : undefined}>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography
                            sx={{ cursor: 'pointer', '&:hover': { color: 'primary.main' } }}
                            onClick={() => navigate(`/candidates/${candidate.id}`)}
                          >
                            {candidate.first_name} {candidate.last_name}
                          </Typography>
                          {candidate.is_archived && (
                            <Chip label="Archived" size="small" variant="outlined" color="default" />
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>{candidate.position}</TableCell>
                      <TableCell>{candidate.location || '-'}</TableCell>
                      <TableCell>
                        {candidate.source ? sourceLabels[candidate.source] : '-'}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={statusLabels[candidate.status]}
                          color={statusColors[candidate.status]}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>{formatNextInterview(candidate.next_interview)}</TableCell>
                      <TableCell align="right">
                        <Tooltip title="View Details">
                          <IconButton
                            size="small"
                            onClick={() => navigate(`/candidates/${candidate.id}`)}
                          >
                            <ViewIcon />
                          </IconButton>
                        </Tooltip>
                        {isManager && !['hired', 'rejected', 'withdrawn'].includes(candidate.status) && (
                          <Tooltip title="Convert to New Hire">
                            <IconButton
                              size="small"
                              color="success"
                              onClick={() => {
                                setSelectedCandidate(candidate);
                                // Find matching position type based on candidate's position
                                const matchingPositionType = positionTypes.find(
                                  (pt: PositionType) => pt.name === candidate.position
                                );
                                setHireData({
                                  hire_date: new Date().toISOString().split('T')[0],
                                  position_type_id: matchingPositionType?.id,
                                  department: '',
                                  manager_id: undefined,
                                });
                                setHireDialogOpen(true);
                              }}
                            >
                              <HireIcon />
                            </IconButton>
                          </Tooltip>
                        )}
                        {isManager && (
                          <Tooltip title={candidate.is_archived ? 'Unarchive' : 'Archive'}>
                            <IconButton
                              size="small"
                              onClick={() => handleArchive(candidate)}
                            >
                              {candidate.is_archived ? <UnarchiveIcon /> : <ArchiveIcon />}
                            </IconButton>
                          </Tooltip>
                        )}
                        {isManager && (
                          <Tooltip title="Delete">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleDelete(candidate)}
                              disabled={candidate.status === 'hired'}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Tooltip>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                  {data?.candidates.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={7} align="center">
                        No candidates found
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
              <TablePagination
                component="div"
                count={data?.total || 0}
                page={page}
                onPageChange={(_, newPage) => setPage(newPage)}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={(e) => {
                  setRowsPerPage(parseInt(e.target.value, 10));
                  setPage(0);
                }}
                rowsPerPageOptions={[10, 25, 50]}
              />
            </>
          )}
        </TableContainer>
      </Box>

      {/* Add Candidate Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Add New Candidate</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="First Name"
                value={formData.first_name}
                onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Last Name"
                value={formData.last_name}
                onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth required>
                <InputLabel>Position</InputLabel>
                <Select
                  value={formData.position}
                  label="Position"
                  onChange={(e) => setFormData({ ...formData, position: e.target.value })}
                >
                  {positionTypes.map((pt: PositionType) => (
                    <MenuItem key={pt.id} value={pt.name}>
                      {pt.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Location</InputLabel>
                <Select
                  value={formData.location || ''}
                  label="Location"
                  onChange={(e) =>
                    setFormData({ ...formData, location: e.target.value || undefined })
                  }
                >
                  <MenuItem value="">None</MenuItem>
                  {(formData.location && !locationOptions.includes(formData.location)
                    ? [formData.location, ...locationOptions]
                    : locationOptions
                  ).map((loc) => (
                    <MenuItem key={loc} value={loc}>
                      {loc}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Phone"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Source</InputLabel>
                <Select
                  value={formData.source || ''}
                  label="Source"
                  onChange={(e) =>
                    setFormData({ ...formData, source: e.target.value as CandidateSource || undefined })
                  }
                >
                  <MenuItem value="">None</MenuItem>
                  {Object.entries(sourceLabels).map(([value, label]) => (
                    <MenuItem key={value} value={value}>
                      {label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Notes"
                multiline
                rows={3}
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <input
                  accept=".pdf,.doc,.docx"
                  style={{ display: 'none' }}
                  id="resume-upload-create"
                  type="file"
                  onChange={(e) => setResumeFile(e.target.files?.[0] || null)}
                />
                <label htmlFor="resume-upload-create">
                  <Button
                    variant="outlined"
                    component="span"
                    startIcon={<UploadIcon />}
                  >
                    {resumeFile ? 'Change Resume' : 'Attach Resume'}
                  </Button>
                </label>
                {resumeFile && (
                  <Chip
                    icon={<ResumeIcon />}
                    label={resumeFile.name}
                    onDelete={() => setResumeFile(null)}
                    deleteIcon={<CloseIcon />}
                  />
                )}
              </Box>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Creating...' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Hire Dialog */}
      <Dialog open={hireDialogOpen} onClose={() => setHireDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Convert to New Hire</DialogTitle>
        <DialogContent>
          {selectedCandidate && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="body1" sx={{ mb: 2 }}>
                Convert <strong>{selectedCandidate.first_name} {selectedCandidate.last_name}</strong> to a new hire?
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Hire Date"
                    type="date"
                    value={hireData.hire_date}
                    onChange={(e) => setHireData({ ...hireData, hire_date: e.target.value })}
                    InputLabelProps={{ shrink: true }}
                    required
                  />
                </Grid>
                <Grid item xs={12}>
                  <FormControl fullWidth required>
                    <InputLabel>Position Type</InputLabel>
                    <Select
                      value={hireData.position_type_id || ''}
                      label="Position Type"
                      onChange={(e) =>
                        setHireData({ ...hireData, position_type_id: e.target.value as number || undefined })
                      }
                    >
                      {positionTypes.map((pt: PositionType) => (
                        <MenuItem key={pt.id} value={pt.id}>
                          {pt.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Department"
                    value={hireData.department}
                    onChange={(e) => setHireData({ ...hireData, department: e.target.value })}
                  />
                </Grid>
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setHireDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            color="success"
            onClick={handleHire}
            disabled={hireMutation.isLoading}
          >
            {hireMutation.isLoading ? 'Converting...' : 'Convert to New Hire'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default Candidates;
